import type { FC, ReactNode } from 'react'
import type { BlogPostSummary } from '@/types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'

interface ArticleIndexProps {
    posts: BlogPostSummary[]
}

/**
 * Articles as a typeset index. On desktop the cover follows the pointer and
 * swaps to the row under it; on touch screens each row carries a thumbnail.
 */
export const ArticleIndex: FC<ArticleIndexProps> = ({ posts }): ReactNode => {
    const list = useRef<HTMLOListElement>(null)
    const floater = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(0)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const host = list.current
        const el = floater.current

        if (!host || !el) return
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

        let x = 0
        let y = 0
        let cx = 0
        let cy = 0
        let raf = 0
        let running = false

        const loop = (): void => {
            cx += (x - cx) * 0.14
            cy += (y - cy) * 0.14

            const tilt = Math.max(-6, Math.min(6, (x - cx) * 0.04))

            el.style.transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0) rotate(${tilt.toFixed(2)}deg)`
            raf = window.requestAnimationFrame(loop)
        }

        const move = (event: PointerEvent): void => {
            x = event.clientX
            y = event.clientY

            if (!running) {
                cx = x
                cy = y
                running = true
                raf = window.requestAnimationFrame(loop)
            }
        }

        const leave = (): void => {
            running = false
            window.cancelAnimationFrame(raf)
        }

        host.addEventListener('pointermove', move)
        host.addEventListener('pointerleave', leave)

        return () => {
            host.removeEventListener('pointermove', move)
            host.removeEventListener('pointerleave', leave)
            window.cancelAnimationFrame(raf)
        }
    }, [])

    if (!posts.length) return null

    return (
        <>
            <ol
                ref={list}
                className='border-t border-tone-fg'
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
            >
                {posts.map((post, index) => (
                    <li key={post.id} className='border-b border-tone-line'>
                        <article className='article-row group relative grid grid-cols-12 items-baseline gap-x-6 gap-y-2 py-6 md:py-8'>
                            <span className='t-label tabular col-span-12 text-tone-muted md:col-span-2'>
                                {formatDate(post.publishedAt)}
                            </span>

                            <h3 className='article-row-title col-span-9 md:col-span-7'>
                                <Link
                                    href={`/blogg/${post.slug}`}
                                    className='after:absolute after:inset-0 focus-visible:outline-none'
                                    onMouseEnter={() => setActive(index)}
                                    onFocus={() => setActive(index)}
                                    data-cursor='Les'
                                >
                                    <span className='relative z-10' data-mdlfy-cms={`blog_posts:${post.id}:title`}>{post.title}</span>
                                </Link>
                            </h3>

                            <span className='col-span-3 flex items-start justify-end gap-4 md:col-span-3 md:items-baseline md:justify-between'>
                                <span className='t-label relative z-10 hidden flex-col gap-1 text-tone-muted md:flex'>
                                    {post.category && (
                                        <span className='text-tone-ink' data-mdlfy-cms={`blog_categories:${post.category.id}:name`}>{post.category.name}</span>
                                    )}
                                    {post.readTime && (
                                        <span>
                                            <span data-mdlfy-cms={`blog_posts:${post.id}:read_time`}>{post.readTime}</span> min
                                        </span>
                                    )}
                                </span>

                                <span className='block size-16 shrink-0 overflow-hidden bg-tone-line md:hidden' aria-hidden='true'>
                                    {post.coverImage && <img src={post.coverImage} alt='' loading='lazy' decoding='async' className='size-full object-cover' />}
                                </span>

                                <ArrowUpRight className='article-row-arrow hidden size-6 shrink-0 md:block' aria-hidden='true' />
                            </span>
                        </article>
                    </li>
                ))}
            </ol>

            <div ref={floater} className='tool-floater pointer-events-none fixed left-0 top-0 z-30 hidden lg:block' aria-hidden='true'>
                <div className={cn('tool-floater-frame', visible && 'is-visible')}>
                    {posts[active]?.coverImage && (
                        <img
                            key={posts[active].id}
                            src={posts[active].coverImage ?? ''}
                            alt=''
                            decoding='async'
                            className='animate-in fade-in zoom-in-105 absolute inset-0 size-full object-cover duration-500'
                        />
                    )}
                </div>
            </div>
        </>
    )
}
